<template>
    <div id="workflow-permission">
        <supsis-table
            :columns="cols"
            :rows="getFilteredRows()"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor || 10"
            :options="tableOptions"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
            class="users-supsis-table"
        >
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <a-input-search
                            :value="searchText"
                            @input="searchTextHandle($event)"
                            :placeholder="$t('message.search')"
                            class="search-input"
                            @search="onSearch"
                        />
                    </div>
                </div>
            </template>
        </supsis-table>
    </div>
</template>

<script>
import SupsisTable from "../SupsisTable.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import AgentAvatar from "../common/AgentAvatar.vue";
import ActionEdit from "../svg/ActionEdit.vue";
import Modal2 from "../common/Modal2.vue";
import * as _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { addTaskPermission, removeTaskPermission } from "../../api/tasks";

export default {
    name: "PermissionWorkflow",
    props: {
        details: {
            type: Object,
        },
    },
    components: {
        ActionEdit,
        SupsisButton,
        SupsisTable,
        Modal2,
        AgentAvatar,
    },
    data() {
        return {
            searchText: "",
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
            tableOptions: {
                id: "users-table",
                scrollWidth: "1000px",
                rowStyleOption: {
                    stripe: true,
                },
                eventCustomOption: {
                    bodyCellEvents: () => {
                        return {
                            dblclick: (event) => {
                                const columnIndex = event.target.cellIndex;
                                const column = this.cols[columnIndex];
                                if (!column || column.copyDisabled) {
                                    return;
                                }

                                let text = event.toElement.innerText;
                                let textArea = document.createElement("textarea");
                                textArea.value = text;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand("copy");
                                document.body.removeChild(textArea);
                                this.$message.success({
                                    content: this.$t("message.copiedClipboard"),
                                    duration: 2,
                                    key: "saved",
                                });
                            },
                        };
                    },
                },
            },
            users: {
                total: 0,
                count: 0,
                data: [],
            },
            currentSort: {
                columnKey: this.$route.query.columnKey || "name",
                order: this.$route.query.order || "ascend",
            },
            form: {
                permissions: {
                    deleteTask: [],
                    editTask: [],
                    createTask: [],
                    draggingTask: [],
                    archiveTask: [],
                    doneTask: [],
                },
            },
            selectedUser: {},
            loadingStates: {},
        };
    },
    computed: {
        ...mapState({
            roles: (state) => state.role.roles,
            subjects: (state) => state.subject.subjects.data,
            loggedUser: (state) => state.auth.user || state.auth.adminUser,
            activeUserCount: (state) => state.user.activeUserCount,
            workflows: (state) => state.tasks.workflows,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        cols() {
            return [
                {
                    field: "Avatar",
                    key: "avatar",
                    title: "",
                    width: 50,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(AgentAvatar, {
                            props: {
                                userID: row._id,
                                size: 30,
                                isNamed: false,
                            },
                        });
                    },
                },
                {
                    field: "name",
                    key: "name",
                    title: this.$t("message.username"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return row.name.length > 15 ? row.name.slice(0, 11) + "..." : row.name;
                    },
                },
                {
                    field: "deleteTask",
                    key: "deleteTask",
                    title: this.$t("task.deleteCard"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isLoading = this.loadingStates[`deleteTask_${row._id}`];
                        const isChecked = this.isHavePermission("deleteTask", row._id);

                        return h("div", { class: "checkbox-wrapper" }, [
                            isLoading
                                ? h("div", { class: "spinner" })
                                : h("div", {
                                      class: ["custom-checkbox", isChecked ? "checked" : ""],
                                      on: {
                                          click: () =>
                                              this.toggleDeleteTaskPermission("deleteTask", row._id, !isChecked),
                                      },
                                  }),
                        ]);
                    },
                },
                {
                    field: "createTask",
                    key: "createTask",
                    title: this.$t("task.createCard"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isLoading = this.loadingStates[`createTask_${row._id}`];
                        const isChecked = this.isHavePermission("createTask", row._id);

                        return h("div", { class: "checkbox-wrapper" }, [
                            isLoading
                                ? h("div", { class: "spinner" })
                                : h("div", {
                                      class: ["custom-checkbox", isChecked ? "checked" : ""],
                                      on: {
                                          click: () =>
                                              this.toggleDeleteTaskPermission("createTask", row._id, !isChecked),
                                      },
                                  }),
                        ]);
                    },
                },
                {
                    field: "editTask",
                    key: "editTask",
                    title: this.$t("task.editCard"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isLoading = this.loadingStates[`editTask_${row._id}`];
                        const isChecked = this.isHavePermission("editTask", row._id);

                        return h("div", { class: "checkbox-wrapper" }, [
                            isLoading
                                ? h("div", { class: "spinner" })
                                : h("div", {
                                      class: ["custom-checkbox", isChecked ? "checked" : ""],
                                      on: {
                                          click: () => this.toggleDeleteTaskPermission("editTask", row._id, !isChecked),
                                      },
                                  }),
                        ]);
                    },
                },
                {
                    field: "draggingTask",
                    key: "draggingTask",
                    title: this.$t("task.dragCard"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isLoading = this.loadingStates[`draggingTask_${row._id}`];
                        const isChecked = this.isHavePermission("draggingTask", row._id);

                        return h("div", { class: "checkbox-wrapper" }, [
                            isLoading
                                ? h("div", { class: "spinner" })
                                : h("div", {
                                      class: ["custom-checkbox", isChecked ? "checked" : ""],
                                      on: {
                                          click: () =>
                                              this.toggleDeleteTaskPermission("draggingTask", row._id, !isChecked),
                                      },
                                  }),
                        ]);
                    },
                },
                {
                    field: "archiveTask",
                    key: "archiveTask",
                    title: "Kart Arşivleme",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isLoading = this.loadingStates[`archiveTask_${row._id}`];
                        const isChecked = this.isHavePermission("archiveTask", row._id);

                        return h("div", { class: "checkbox-wrapper" }, [
                            isLoading
                                ? h("div", { class: "spinner" })
                                : h("div", {
                                      class: ["custom-checkbox", isChecked ? "checked" : ""],
                                      on: {
                                          click: () =>
                                              this.toggleDeleteTaskPermission("archiveTask", row._id, !isChecked),
                                      },
                                  }),
                        ]);
                    },
                },
                {
                    field: "doneTask",
                    key: "doneTask",
                    title: "Kart Tamamlama",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isLoading = this.loadingStates[`doneTask_${row._id}`];
                        const isChecked = this.isHavePermission("doneTask", row._id);

                        return h("div", { class: "checkbox-wrapper" }, [
                            isLoading
                                ? h("div", { class: "spinner" })
                                : h("div", {
                                      class: ["custom-checkbox", isChecked ? "checked" : ""],
                                      on: {
                                          click: () => this.toggleDeleteTaskPermission("doneTask", row._id, !isChecked),
                                      },
                                  }),
                        ]);
                    },
                },
            ];
        },
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            getRoles: "role/getRoles",
            getActiveUserCount: "user/getActiveUserCount",
            setWorkflows: "tasks/setWorkflows",
        }),
        getFilteredRows() {
            const getWorkflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
            const users = this.users.data;

            if (!getWorkflow) {
                return [];
            }

            const visibleUsers = getWorkflow.visibility && getWorkflow.visibility.users;
            const query = this.searchText?.toLowerCase();

            if (visibleUsers && visibleUsers.length > 0) {
                let filteredUsers = users.filter((user) => visibleUsers.includes(user._id));

                if (query) {
                    filteredUsers = filteredUsers.filter(user =>
                        user.name.toLowerCase().includes(query) ||
                        user.email?.toLowerCase().includes(query)
                    );
                }

                return filteredUsers;
            } else {
                return users;
            }
        },
        searchTextHandle(e) {
            this.searchText = e.target.value;
        },
        onSearch() {
            this.paginationCursor.page = 1;
            this.get(false, true);
        },
        isHavePermission(permissionType, userId) {
            return (this.form.permissions[permissionType] || []).includes(userId);
        },
        toggleDeleteTaskPermission(permissionType, userId, checked) {
            this.$set(this.loadingStates, `${permissionType}_${userId}`, true);

            const promiseFn = checked ? addTaskPermission : removeTaskPermission;
            promiseFn({
                workflowId: this.details.workflowId,
                userId,
                permissionType,
            })
                .then((workflow) => {
                    this.setWorkflows(
                        this.workflows.map((x) => {
                            if (x._id === this.details.workflowId) {
                                x.permissions = workflow.permissions;
                            }
                            return x;
                        })
                    );
                })
                .catch(() => {
                    this.$message.error("Kullanıcının yetkileri güncellenirken bir hata oluştu.");
                })
                .finally(() => {
                    this.$set(this.loadingStates, `${permissionType}_${userId}`, false);
                });
        },
        debouncedGet(getRoles = false, showLoading = true, interval = 0) {
            if (this.getTimerId) clearTimeout(this.getTimerId);
            this.getTimerId = setTimeout(() => {
                this.get(getRoles, showLoading);
            }, interval);
        },
        get(getRoles = false, showLoading = true) {
            if (showLoading) this.isBusy = true;
            const query = this.searchText === "" && this.searchText == null ? undefined : this.searchText;
            const sorting = {
                sortBy: this.currentSort.columnKey,
                sortOrder: this.currentSort.order,
            };

            const getWorkflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
            const visibleUsers = getWorkflow && getWorkflow.visibility && getWorkflow.visibility.users;

            if (query && visibleUsers && visibleUsers.length > 0) {
                this.getUsers({
                    perPage: 1000,
                    page: 1,
                    query,
                    sortBy: sorting.sortBy,
                    sortOrder: sorting.sortOrder,
                    updateStore: false,
                })
                .then((x) => {
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;

                        const filteredCount = x.data.data.filter(user =>
                            visibleUsers.includes(user._id) &&
                            (user.name.toLowerCase().includes(query.toLowerCase()) ||
                             user.email?.toLowerCase().includes(query.toLowerCase()))
                        ).length;

                        this.paginationCursor.total = filteredCount;

                        if (filteredCount <= this.paginationCursor.perPage && this.paginationCursor.page > 1) {
                            this.paginationCursor.page = 1;
                        }
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (showLoading) this.isBusy = false;
                    this.lastFetchTS = Date.now();
                });
            } else if (visibleUsers && visibleUsers.length > 0) {
                this.getUsers({
                    perPage: 100,
                    page: 1,
                    query: undefined,
                    sortBy: sorting.sortBy,
                    sortOrder: sorting.sortOrder,
                    updateStore: false,
                })
                .then((x) => {
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;

                        const filteredCount = x.data.data.filter(user =>
                            visibleUsers.includes(user._id)).length;
                        this.paginationCursor.total = filteredCount;

                        if (filteredCount <= this.paginationCursor.perPage && this.paginationCursor.page > 1) {
                            this.paginationCursor.page = 1;
                        }
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (showLoading) this.isBusy = false;
                    this.lastFetchTS = Date.now();
                });
            } else {
                this.getUsers({
                    perPage: this.paginationCursor.perPage,
                    page: this.paginationCursor.page,
                    query,
                    sortBy: sorting.sortBy,
                    sortOrder: sorting.sortOrder,
                    updateStore: false,
                })
                .then((x) => {
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;
                        this.paginationCursor.total = x.data.total;
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (showLoading) this.isBusy = false;
                    this.lastFetchTS = Date.now();
                });
            }

            this.getActiveUserCount();
        },
    },
    mounted() {
        const workflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
        this.form.permissions = workflow.permissions || this.form.permissions;
        this.get(true);
        this.lastFetchTS = Date.now();
        this.timerId = setInterval(() => {
            const tookTime = Date.now() - this.lastFetchTS;
            if (tookTime > 500) {
                this.debouncedGet(false, false, 0);
            }
        }, 500);
    },
    beforeDestroy() {
        if (this.timerId) clearInterval(this.timerId);
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal.page !== oldVal.page) {
                    this.get(false, true);
                }
            },
        },
        searchText(newValue, oldValue) {
            this.paginationCursor.page = 1;
            this.debouncedGet();
        },
        currentSort(newValue, oldValue) {
            if (newValue) {
                this.get(false, true);
            }
        },
        workflows: {
            deep: true,
            handler(newVal, oldVal) {
                const workflow = newVal.find((workflow) => workflow._id === this.details.workflowId);
                this.form.permissions = workflow.permissions || this.form.permissions;
            },
        },
    },
};
</script>
<style lang="scss" scoped>
#workflow-permission {
    overflow: scroll;
    height: 650px;

    .permissions-container {
        padding: 20px;
    }

    .modal2 {
        width: 400px !important;
    }

    .username {
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        margin-bottom: 20px;
    }

    .checkbox-group {
        flex-direction: column;
        display: grid;
        gap: 10px;
    }

    .checkbox-group label {
        display: flex;
        align-items: center;
        font-size: 16px;
        padding: 5px 0;
    }

    .checkbox-group input[type="checkbox"] {
        margin-right: 10px;
        accent-color: #1e88e5;
    }
}
</style>

<style lang="scss">
.checkbox-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 5px 0;
    min-height: 40px;

    .custom-checkbox {
        position: relative;
        width: 24px;
        height: 24px;
        min-width: 24px;
        min-height: 24px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        transition: all 0.3s;
        background-color: transparent;
        box-sizing: border-box;

        &:hover {
            &:after {
                content: "visibility";
                font-family: "Material Icons";
                position: absolute;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 18px;
                color: #666;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
            }
        }

        &.checked {
            &:before {
                content: "";
                position: absolute;
                display: block;
                width: 20px;
                height: 20px;
                background-color: transparent;
            }

            &:after {
                content: "";
                position: absolute;
                display: block;
                width: 6px;
                height: 12px;
                border: solid #4caf50;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
                left: 9px;
                top: 5px;
            }
        }

        &:not(.checked):not(:hover) {
            &:after {
                content: "";
                position: absolute;
                display: block;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background-color: #666;
                opacity: 0.5;
                left: 3px;
                top: 3px;
            }
        }
    }

    .spinner {
        width: 24px;
        height: 24px;
        min-width: 24px;
        min-height: 24px;
        border: 2px solid rgba(30, 136, 229, 0.2);
        border-radius: 50%;
        border-top: 2px solid #1e88e5;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
